import { useCallback, useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { headingsPlugin, listsPlugin, MDXEditor, quotePlugin, thematicBreakPlugin } from "@mdxeditor/editor";
import { decodeTags, encodeTags } from "@/utils/utils";

interface MdxEditorMethods {
  readonly getMarkdown: () => string;
  readonly setMarkdown: (value: string) => void;
  readonly insertMarkdown: (value: string) => void;
  readonly focus: (callbackFn?: () => void, opts?: { readonly defaultSelection?: "rootStart" | "rootEnd"; readonly preventScroll?: boolean }) => void;
  readonly getContentEditableHTML: () => string;
  readonly getSelectionMarkdown: () => string;
}

interface MdxEditorFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly contentEditableClassName?: string;
  /** When true, the editor is not editable and blocks interaction. */
  readonly disabled?: boolean;
  /** Not editable, but the text can still be selected and copied. */
  readonly readOnly?: boolean;
  /**
   * When this value changes, we will call `editor.setMarkdown(value)` to keep the
   * editor's internal state in sync (MDXEditor may not update reliably from props alone).
   */
  readonly syncKey?: string;
}

const MdxEditorField = ({
  value,
  onChange,
  placeholder,
  contentEditableClassName = "mdx-content",
  disabled = false,
  readOnly = false,
  syncKey
}: MdxEditorFieldProps) => {
  const editorRef = useRef<MdxEditorMethods | null>(null);

  const plugins = useMemo(() => [headingsPlugin(), quotePlugin(), thematicBreakPlugin(), listsPlugin()], []);

  const encodedValue = useMemo(() => encodeTags(value), [value]);

  const handleChange = useCallback(
    (newMarkdown: string) => {
      onChange(decodeTags(newMarkdown));
    },
    [onChange]
  );

  useEffect(() => {
    editorRef.current?.setMarkdown(encodedValue);
  }, [syncKey, encodedValue]);

  return (
    <Box sx={{ position: "relative" }}>
      <MDXEditor
        ref={editorRef}
        markdown={encodedValue}
        onChange={handleChange}
        placeholder={placeholder}
        contentEditableClassName={contentEditableClassName}
        plugins={plugins}
        readOnly={readOnly || disabled}
      />
      {disabled ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            cursor: "not-allowed",
            zIndex: 1
          }}
        />
      ) : null}
    </Box>
  );
};

export default MdxEditorField;
